import { reaction } from "mobx";
import { AppUpload, type IAppUpload, type IUploadStores } from "./AppUpload";
import { type IUploadStore, UploadStore } from "./UploadStore";
import { type IUploadCodeAndSourceSelectionStore, UploadCodeAndSourceSelectionStore } from "./content/upload-code-and-source-selection/UploadCodeAndSourceSelectionStore";
import { CodeAndSourceValidationFetcher } from "../network/fetchers/CodeAndSourceValidationFetcher";
import type { IFetcher } from "../util/io/Fetcher";
import { type IPatientUploadCompleteCallbackStore, PatientUploadCompleteCallbackStore } from "./PatientUploadCompleteCallbackStore";
import { DicomUploadTusFingerprintStore } from "./DicomUploadTusFingerprintStore";
import { CurrentUploadSettingsStore } from "./CurrentUploadSettingsStore";
import { WindowLocalStorage } from "../util/io/LocalStorage";
import type { IAppStoresFactory } from "../shared-components/AppStoresFactory";
import type { IInitialResourcesLoader } from "../resources/InitialResourcesLoader";
import type { UploadIndexViewModelModel } from "../../OpenApi/api";

export interface IAppUploadFactory {
    create(uploadViewModel: UploadIndexViewModelModel, initialResourcesLoader: IInitialResourcesLoader): Promise<IAppUpload>;
}

export class AppUploadFactory implements IAppUploadFactory {
    constructor(
        private readonly fetcher: IFetcher,
        private readonly appStoresFactory: IAppStoresFactory,
    ) {}

    public async create(uploadViewModel: UploadIndexViewModelModel, initialResourcesLoader: IInitialResourcesLoader): Promise<IAppUpload> {
        const { fetcher, appStoresFactory } = this;

        const appStores = await appStoresFactory.create(uploadViewModel.layout, initialResourcesLoader);

        /* stores */
        const currentUploadSettingsStore = new CurrentUploadSettingsStore(uploadViewModel);
        const uploadCodeAndSourceSelectionStore: IUploadCodeAndSourceSelectionStore = new UploadCodeAndSourceSelectionStore(
            new CodeAndSourceValidationFetcher(fetcher),
            currentUploadSettingsStore,
        );

        // On the upload page the "back to login" link should point at the source that is currently selected here.
        reaction(
            () => uploadCodeAndSourceSelectionStore.selectedSourceForUpload?.name ?? null,
            sourceName => appStores.backToLoginStore.setSourceOverride(sourceName),
            { fireImmediately: true },
        );

        const patientUploadCompleteCallbackStore: IPatientUploadCompleteCallbackStore = new PatientUploadCompleteCallbackStore(fetcher);
        const dicomUploadTusFingerprintStore = new DicomUploadTusFingerprintStore(new WindowLocalStorage(window));

        const uploadStore: IUploadStore = new UploadStore(
            uploadCodeAndSourceSelectionStore,
            patientUploadCompleteCallbackStore,
            dicomUploadTusFingerprintStore,
            currentUploadSettingsStore,
        );

        const UploadStores: IUploadStores = {
            ...appStores,
            uploadStore,
            uploadCodeAndSourceSelectionStore,
            dicomUploadTusFingerprintStore,
            currentUploadSettingsStore,
        };

        return new AppUpload(UploadStores);
    }
}
